import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, GeoJSON } from 'react-leaflet';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import L from 'leaflet';
import Select from 'react-select';
import { WASTE_CATEGORIES, getCategoryColor, getCategoryKeys } from '../config/wasteCategories';
import { format } from 'date-fns';
import { FaExpand, FaCompress, FaFilter } from 'react-icons/fa';
import './DashboardMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const createColoredIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 20px; height: 20px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -20]
  });
};

const createCleanedIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: white; width: 20px; height: 20px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid #10b981; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -20]
  });
};

const createFoodDonationIcon = (claimed) => {
  const bg = claimed ? '#9ca3af' : '#22c55e';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${bg}; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); display:flex; align-items:center; justify-content:center; font-size:14px;">🍲</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const createCharityItemIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: #8b5cf6; width: 24px; height: 24px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); display:flex; align-items:center; justify-content:center; font-size:14px;">🎁</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 30;
  let color = '#10b981';
  
  if (count >= 20) {
    size = 40;
    color = '#ef4444';
  } else if (count >= 10) {
    size = 35;
    color = '#f59e0b';
  }
  
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: ${size/3}px; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

function MarkerClusterLayer({ reports, onMarkerClick }) {
  const map = useMap();
  const clusterGroupRef = useRef(null);

  useEffect(() => {
    if (!map) return;
    
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    const markerClusterGroup = L.markerClusterGroup({
      iconCreateFunction: createClusterIcon,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      disableClusteringAtZoom: 15
    });

    reports.forEach(report => {
      if (!report.location) return;
      
      const loc = typeof report.location === 'string' ? JSON.parse(report.location) : report.location;
      if (!loc.lat || !loc.lng) return;
      
      let icon;
      if (report.category === 'food_donation') {
        icon = createFoodDonationIcon(report.claimed_status === 'claimed');
      } else if (report.category === 'charity_items') {
        icon = createCharityItemIcon();
      } else if (report.status === 'cleaned') {
        icon = createCleanedIcon();
      } else {
        icon = createColoredIcon(getCategoryColor(report.category));
      }
      
      const marker = L.marker([loc.lat, loc.lng], { icon });
      
      const categoryName = WASTE_CATEGORIES[report.category]?.name || report.category;
      let extra = '';
      if (report.category === 'food_donation' && report.claimed_status) {
        const statusColor = report.claimed_status === 'claimed' ? '#ef4444' : report.claimed_status === 'expired' ? '#6b7280' : '#22c55e';
        extra = `<span style="background:${statusColor};color:white;padding:1px 5px;border-radius:3px;font-size:10px;font-weight:bold;">${report.claimed_status.toUpperCase()}</span>`;
      } else if (report.category === 'charity_items' && report.item_condition) {
        extra = `<span style="color:#8b5cf6;">${'★'.repeat(report.item_condition)}${'☆'.repeat(5 - report.item_condition)}</span>`;
      }
      marker.bindPopup(`
        <div class="map-popup">
          <strong>${categoryName}</strong>
          <p>${report.wasteType || report.waste_type}</p>
          ${extra}
          <small>${format(new Date(report.timestamp), 'dd MMM yyyy')}</small>
          <span class="popup-status ${report.status}">${report.status}</span>
        </div>
      `);
      
      if (onMarkerClick) {
        marker.on('click', () => onMarkerClick(report));
      }
      
      markerClusterGroup.addLayer(marker);
    });

    map.addLayer(markerClusterGroup);
    clusterGroupRef.current = markerClusterGroup;

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
  }, [map, reports, onMarkerClick]);

  return null;
}

function MapBounds({ center, zoom, boundary }) {
  const map = useMap();
  
  useEffect(() => {
    if (boundary) {
      try {
        const geoJson = L.geoJSON(boundary);
        map.fitBounds(geoJson.getBounds(), { padding: [20, 20] });
      } catch (e) {
        console.error('Error fitting bounds:', e);
      }
    } else if (center) {
      map.setView(center, zoom || 12);
    }
  }, [map, center, zoom, boundary]);
  
  return null;
}

const DashboardMap = ({ 
  reports = [], 
  center = [51.5074, -0.1278], 
  zoom = 10,
  boundary = null,
  radius = null,
  showFilters = true,
  showLocalAuthorityFilter = false,
  localAuthorities = [],
  onMarkerClick = null,
  onLocalAuthorityChange = null,
  title = 'Waste Map',
  className = ''
}) => {
  const [expanded, setExpanded] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [localAuthorityFilter, setLocalAuthorityFilter] = useState(null);

  const localAuthorityOptions = [
    { value: 'all', label: 'All Local Authorities' },
    ...localAuthorities.map(la => ({ value: la, label: la }))
  ];

  const handleLocalAuthorityChange = (option) => {
    setLocalAuthorityFilter(option);
    if (onLocalAuthorityChange) {
      onLocalAuthorityChange(option?.value === 'all' ? null : option?.value);
    }
  };

  const filteredReports = reports.filter(report => {
    if (categoryFilter !== 'all' && report.category !== categoryFilter) return false;
    if (statusFilter !== 'all' && report.status !== statusFilter) return false;
    return true;
  });

  const allCategories = getCategoryKeys();

  const boundaryStyle = {
    color: '#3b82f6',
    weight: 3,
    fillColor: '#3b82f6',
    fillOpacity: 0.1
  };

  return (
    <div className={`dashboard-map-container ${expanded ? 'expanded' : ''} ${className}`}>
      <div className="map-header">
        <h3>{title}</h3>
        <div className="map-controls">
          {showFilters && (
            <div className="map-filters">
              {showLocalAuthorityFilter && localAuthorities.length > 0 && (
                <Select
                  value={localAuthorityFilter || localAuthorityOptions[0]}
                  onChange={handleLocalAuthorityChange}
                  options={localAuthorityOptions}
                  isSearchable={true}
                  placeholder="Filter by Local Authority..."
                  className="la-filter-select"
                  classNamePrefix="la-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minWidth: '200px',
                      fontSize: '0.85rem',
                      borderColor: 'rgba(255,255,255,0.3)',
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: 'white',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.5)' }
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: 'white'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: 'rgba(255,255,255,0.7)'
                    }),
                    input: (base) => ({
                      ...base,
                      color: 'white'
                    }),
                    option: (base, state) => ({
                      ...base,
                      fontSize: '0.85rem',
                      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f0f0f0' : 'white',
                      color: state.isSelected ? 'white' : '#333'
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 1000
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: 'white',
                      '&:hover': { color: 'white' }
                    }),
                    indicatorSeparator: () => ({ display: 'none' })
                  }}
                />
              )}
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Categories</option>
                {allCategories.map(cat => (
                  <option key={cat} value={cat}>
                    {WASTE_CATEGORIES[cat]?.name || cat}
                  </option>
                ))}
              </select>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="cleaned">Cleaned</option>
              </select>
            </div>
          )}
          <button 
            className="expand-btn"
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Minimize' : 'Expand'}
          >
            {expanded ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
      </div>
      
      <div className="map-stats">
        <span className="stat-item">
          <span className="stat-count">{filteredReports.length}</span> reports
        </span>
        <span className="stat-item pending">
          <span className="stat-count">{filteredReports.filter(r => r.status === 'pending').length}</span> pending
        </span>
        <span className="stat-item cleaned">
          <span className="stat-count">{filteredReports.filter(r => r.status === 'cleaned').length}</span> cleaned
        </span>
      </div>

      <MapContainer
        center={center}
        zoom={zoom}
        className="dashboard-leaflet-map"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapBounds center={center} zoom={zoom} boundary={boundary} />
        
        {boundary && (
          <GeoJSON key={JSON.stringify(boundary).slice(0, 100)} data={boundary} style={boundaryStyle} />
        )}
        
        {radius && (
          <Circle 
            center={center} 
            radius={radius * 1000}
            pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.1 }}
          />
        )}
        
        <MarkerClusterLayer reports={filteredReports} onMarkerClick={onMarkerClick} />
      </MapContainer>
    </div>
  );
};

export default DashboardMap;
